import React, { useState } from 'react'
import { Button } from '@wove/web-ui'
import { Plus, RefreshCw, AlertTriangle, Settings } from 'lucide-react'
import { toast } from 'sonner'
import CustomFieldsTable from './CustomFieldsTable'
import CustomFieldForm from './CustomFieldForm'
import { useCustomFields, useIntegrations, CustomFieldDefinition, CreateCustomFieldDefinition, UpdateCustomFieldDefinition } from '@wove/api'

interface CustomFieldsProps {
  integrationId?: string
}

export default function CustomFields({ integrationId: providedIntegrationId }: CustomFieldsProps) {
  const { integrations, isLoading: integrationsLoading, error: integrationsError } = useIntegrations()
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<string>('')

  // Use provided integration ID or manage selection locally
  const effectiveIntegrationId = providedIntegrationId || selectedIntegrationId

  // Set default integration when data loads (only if no integration provided)
  React.useEffect(() => {
    if (!providedIntegrationId && integrations.length > 0 && !selectedIntegrationId) {
      setSelectedIntegrationId(integrations[0].id)
    }
  }, [integrations, selectedIntegrationId, providedIntegrationId])
  const [showForm, setShowForm] = useState(false)
  const [editingField, setEditingField] = useState<CustomFieldDefinition | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<CustomFieldDefinition | null>(null)

  const {
    fieldDefinitions,
    isLoading,
    error,
    refetch,
    createField,
    updateField,
    deleteField,
    isCreating,
    isUpdating,
    isDeleting,
    reorderFields,
    isReordering,
  } = useCustomFields(effectiveIntegrationId)

  const handleCreateField = async (fieldData: CreateCustomFieldDefinition) => {
    try {
      await createField(fieldData)
      setShowForm(false)
      toast.success('Custom field created successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to create custom field')
    }
  }

  const handleUpdateField = async (fieldData: UpdateCustomFieldDefinition) => {
    if (!editingField) return
    try {
      await updateField(editingField.id, fieldData)
      setEditingField(null)
      setShowForm(false)
      toast.success('Custom field updated successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to update custom field')
    }
  }

  const handleDeleteField = async (field: CustomFieldDefinition) => {
    try {
      await deleteField(field.id)
      setShowDeleteConfirm(null)
      toast.success('Custom field deleted successfully')
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete custom field')
    }
  }

  const openCreateForm = () => {
    setEditingField(null)
    setShowForm(true)
  }

  const openEditForm = (field: CustomFieldDefinition) => {
    setEditingField(field)
    setShowForm(true)
  }

  const closeForm = () => {
    setShowForm(false)
    setEditingField(null)
  }

  // Handle integrations loading/error states (only if no integration provided)
  if (!providedIntegrationId) {
    if (integrationsError) {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Custom Fields</h2>
            <p className="text-muted-foreground">
              Configure custom fields for RFQs and shipments
            </p>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  Error loading integrations
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>{integrationsError.message}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (integrationsLoading) {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Custom Fields</h2>
            <p className="text-muted-foreground">
              Configure custom fields for RFQs and shipments
            </p>
          </div>
          
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Loading integrations...</span>
          </div>
        </div>
      )
    }

    if (integrations.length === 0) {
      return (
        <div className="space-y-6">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Custom Fields</h2>
            <p className="text-muted-foreground">
              Configure custom fields for RFQs and shipments
            </p>
          </div>
          
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-400" />
              <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                No integrations found
              </h3>
                <p>You need to set up an integration before you can configure custom fields.</p>
              </div>
            </div>
          </div>
        </div>
      )
    }
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Custom Fields</h2>
          <p className="text-muted-foreground">
            Configure custom fields for RFQs and shipments
          </p>
        </div>
        
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Error loading custom fields
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error.message}</p>
              </div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  className="text-red-700 border-red-300 hover:bg-red-100"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Try Again
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {!providedIntegrationId && (
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Custom Fields</h2>
          <p className="text-muted-foreground">
            Configure custom fields for RFQs and shipments to capture additional information
          </p>
        </div>
      )}

      {/* Integration Selector - only show when no integration provided */}
      {!providedIntegrationId && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4">
          <div className="flex items-start">
            <Settings className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-blue-800">
                Integration Configuration
              </h3>
              <p className="mt-1 text-sm text-blue-700">
                Custom fields are configured per integration. 
                {integrations.length === 1 
                  ? ` Currently managing fields for: ${integrations[0].name}`
                  : ' Select an integration to manage its custom fields.'
                }
              </p>
              {integrations.length > 1 && (
                <div className="mt-3">
                  <label className="block text-sm font-medium text-blue-800 mb-1">
                    Select Integration:
                  </label>
                  <select
                    value={selectedIntegrationId}
                    onChange={(e) => setSelectedIntegrationId(e.target.value)}
                    className="px-3 py-2 border border-blue-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-64"
                  >
                    {integrations.map((integration) => (
                      <option key={integration.id} value={integration.id}>
                        {integration.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {integrations.length === 1 && (
                <div className="mt-2 text-xs text-blue-600">
                  Integration ID: {selectedIntegrationId}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <h3 className="text-lg font-medium">Field Definitions</h3>
          <span className="text-sm text-gray-500">
            {fieldDefinitions.length} field{fieldDefinitions.length !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            onClick={openCreateForm}
            disabled={isLoading}
          >
            <Plus className="h-4 w-4 mr-2" />
            Create Field
          </Button>
        </div>
      </div>

      {/* Fields Table */}
      <CustomFieldsTable
        fields={fieldDefinitions}
        onEdit={openEditForm}
        onDelete={(field) => setShowDeleteConfirm(field)}
        onReorder={async (appliesTo, reorderedFields) => {
          try {
            const fieldOrders = reorderedFields.map((field, index) => ({
              fieldKey: field.fieldKey,
              fieldOrder: index + 1
            }))
            await reorderFields(appliesTo, fieldOrders)
            toast.success('Field order updated successfully')
          } catch (error: any) {
            toast.error(error.message || 'Failed to reorder fields')
          }
        }}
        isLoading={isLoading || isReordering}
      />

      {/* Create/Edit Form Modal */}
      {showForm && (
        <CustomFieldForm
          field={editingField || undefined}
          onSubmit={async (data) => {
            if (editingField) {
              await handleUpdateField(data as UpdateCustomFieldDefinition)
            } else {
              await handleCreateField(data as CreateCustomFieldDefinition)
            }
          }}
          onCancel={closeForm}
          isSubmitting={isCreating || isUpdating}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full m-4">
            <div className="p-6">
              <div className="flex items-center">
                <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">
                  Delete Custom Field
                </h3>
              </div>
              <div className="mt-2">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete the field "{showDeleteConfirm.fieldName}"? 
                  This will also remove all data stored in this field for existing RFQs and shipments. 
                  This action cannot be undone.
                </p>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <Button
                  variant="outline"
                  onClick={() => setShowDeleteConfirm(null)}
                  disabled={isDeleting}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => handleDeleteField(showDeleteConfirm)}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Field'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}